import { Synchronizer } from '@helpers/javascript/cable/synchronizer'
import { store }        from '@redux/store'

export const syncWorkorderAction = workorder => {
  if (workorder.error) return
  // Offline edits stay pending; a sync attempt now would only fail into a manual retry.
  if (!store.getState().offline.online) return
  // One in-flight Synchronizer per workorder: a second would duplicate the PATCH
  // (re-firing the manager's notifications) and could revert a synced result.
  if (Synchronizer.active.has(workorder.id)) return

  if (!workorder.synced) {
    new Synchronizer({ ...workorder, version: new Date().getTime() }).transmit()
    return
  }

  // A syncing workorder with no live Synchronizer (deferred or lost to a reload) is
  // reconciled: a blind re-send would re-fire the backend callbacks.
  if (workorder.syncing) {
    new Synchronizer(workorder).reconcile()
  }
}

export const syncWorkorderLockAction = workorder => dispatch => {
  dispatch({
    type:    WORKORDER_SYNC.LOCK,
    payload: { workorder }
  })
}

export const syncWorkorderBeginAction = workorder => dispatch => {
  dispatch({
    type:    WORKORDER_SYNC.BEGIN,
    payload: { workorder }
  })
}

export const syncWorkorderSuccessAction = workorder => dispatch => {
  dispatch({
    type:    WORKORDER_SYNC.SUCCESS,
    payload: { workorder }
  })
}

export const syncWorkorderRetryAction = workorder => dispatch => {
  dispatch({
    type: WORKORDER_SYNC.RETRY,
    payload: { workorder }
  })
}

export const syncWorkorderFailureAction = (workorder, error) => dispatch => {
  dispatch({
    type: WORKORDER_SYNC.FAILURE,
    payload: { workorder, error }
  })
}

const MAX_PHOTO_ATTEMPTS = 3

export const releasePhotos = photos =>
  (photos || []).map(photo => photo.synced ? photo : { ...photo, syncing: false })

const replaceRecord = (collection, record) => {
  return collection.map(item => item.id === record.id ? record : item)
}

const replaceWorkorder = (state, workorder) => ({
  ...state,
  items:             replaceRecord(state.items,            workorder),
  stored_workorders: replaceRecord(state.stored_workorders, workorder)
})

export const WORKORDER_SYNC = {
  LOCK:    'WORKORDER_SYNC_LOCK',
  BEGIN:   'WORKORDER_SYNC_BEGIN',
  SUCCESS: 'WORKORDER_SYNC_SUCCESS',
  RETRY:   'WORKORDER_SYNC_RETRY',
  FAILURE: 'WORKORDER_SYNC_FAILURE',

  lockResponse: ({ state, workorder }) => {
    return ({
      ...state,
      items: state.items.map(item => item.id != workorder.id ? item : {
        ...item,
        locked: true
      }
    )})
  },

  beginResponse: ({ state, workorder }) => ({
    ...state,
    items: state.items.map(item => item.id != workorder.id ? item : {
      ...item,
      synced:  true,
      syncing: true,
      locked:  false,
      version: workorder.version,
      retries: 0,
      photos:  item.photos.map(photo => ({ ...photo, syncing: true }))
    })
  }),

  // A local photo is the only copy until the server returns its version.
  successResponse: ({ state, workorder }) => ({
    ...state,
    items: state.items.map(item => {
      if (item.id != workorder.id) return item

      const serverPhotos = workorder.photos || []
      const confirmed    = new Set(serverPhotos.map(photo => String(photo.version)))

      const unsyncedPhotos = (item.photos || [])
        .filter(photo => !photo.synced)
        .filter(photo => photo.version && !photo.id ? !confirmed.has(String(photo.version)) : !photo.syncing)
        .map(photo => photo.syncing ? { ...photo, syncing: false, attempts: (photo.attempts || 0) + 1 } : photo)

      // A photo the server keeps missing is reported instead of re-sent forever.
      const lost = unsyncedPhotos.filter(photo => photo.attempts >= MAX_PHOTO_ATTEMPTS).length

      return {
        ...workorder,
        synced: unsyncedPhotos.length === 0,
        error:  lost > 0 ? I18n.t('mobile.sync.photos_not_sent', { count: lost }) : workorder.error,
        photos: [...serverPhotos, ...unsyncedPhotos]
      }
    })
  }),

  retryResponse: ({ state, workorder }) => ({
    ...state,
    items: state.items.map(item => item.id != workorder.id ? item : {
      ...item,
      retries: (item.retries || 0) + 1
    })
  }),

  failureResponse: ({ state, workorder, error }) => ({
    ...state,
    items: state.items.map(item => item.id != workorder.id ? item : {
      ...item,
      synced:  false,
      syncing: false,
      error,
      photos:  releasePhotos(item.photos)
    })
  })
}


